/**
 * Drizzle ORM + IndexedDB Implementation of ICustomerRepository
 */
import type { ICustomerRepository } from './ICustomerRepository';
import type { Customer } from '../screens/M15CustomerListScreen';
import { getIndexedDb, STORES } from '../db/db.web';
import { generateUUID } from '../core/uuid';
import { calculateInsuranceAge, calculateChronologicalAge } from '../core/calculations';

const SEED_CUSTOMERS: Customer[] = [
  {
    id: 'KH-1001',
    name: 'Hoàng Cao Minh',
    category: 'main',
    dependentsCount: 0,
    dob: '24/01/1992',
    age: 34,
    gender: 'Nam',
    phone: '84333233192',
    email: 'hoangcaominh@gmail.com',
    labels: ['vip'],
    occupation: 'Nhân viên văn phòng',
    createdAt: '2026-06-20',
    updatedAt: '2026-06-20T10:00:00.000Z',
    created_at: '2026-06-20T10:00:00.000Z',
    updated_at: '2026-06-20T10:00:00.000Z',
    isLocal: true,
  },
  {
    id: 'KH-1002',
    name: 'Tạ Minh Luận',
    category: 'main',
    dependentsCount: 1,
    dob: '25/11/1996',
    age: 29,
    gender: 'Nam',
    phone: '0901234567',
    email: 'taminhluan@gmail.com',
    labels: ['potential', 'new'],
    occupation: 'Lập trình viên',
    createdAt: '2026-06-22',
    updatedAt: '2026-06-22T14:30:00.000Z',
    created_at: '2026-06-22T14:30:00.000Z',
    updated_at: '2026-06-22T14:30:00.000Z',
    isLocal: true,
  },
  {
    id: 'KH-1003',
    name: 'Nguyễn Văn A',
    category: 'main',
    dependentsCount: 2,
    dob: '15/03/1985',
    age: 41,
    gender: 'Nam',
    phone: '0912345678',
    email: 'nguyenvana@gmail.com',
    labels: ['loyal', 'family'],
    occupation: 'Kế toán trưởng',
    createdAt: '2026-06-15',
    updatedAt: '2026-06-15T09:15:00.000Z',
    created_at: '2026-06-15T09:15:00.000Z',
    updated_at: '2026-06-15T09:15:00.000Z',
    isLocal: true,
  },
  {
    id: 'KH-1004',
    name: 'Trần Thị B',
    category: 'main',
    dependentsCount: 1,
    dob: '08/09/1982',
    age: 43,
    gender: 'Nữ',
    phone: '0938765432',
    email: 'tranthib@gmail.com',
    labels: ['consulting'],
    occupation: 'Bác sĩ',
    createdAt: '2026-06-18',
    updatedAt: '2026-06-18T16:20:00.000Z',
    created_at: '2026-06-18T16:20:00.000Z',
    updated_at: '2026-06-18T16:20:00.000Z',
    isLocal: true,
  },
];

export class CustomerRepository implements ICustomerRepository {
  private initialized = false;
  private memoryStore: Customer[] = [...SEED_CUSTOMERS];

  private async ensureInitialized(): Promise<void> {
    if (this.initialized) return;
    try {
      const db = await getIndexedDb();
      if (db) {
        const count = await db.count(STORES.CUSTOMERS);
        if (count === 0) {
          const tx = db.transaction(STORES.CUSTOMERS, 'readwrite');
          for (const customer of SEED_CUSTOMERS) {
            await tx.store.put(customer);
          }
          await tx.done;
        }
        this.initialized = true;
      }
    } catch (err) {
      // IndexedDB not available
    }
  }

  async getAll(): Promise<Customer[]> {
    await this.ensureInitialized();
    let list: Customer[] = [];
    try {
      const db = await getIndexedDb();
      list = await db.getAll(STORES.CUSTOMERS);
    } catch (err) {
      list = this.memoryStore;
    }
    return (list || []).map((c) => ({
      ...c,
      age: c.dob ? calculateChronologicalAge(c.dob) : c.age,
    }));
  }

  async getById(id: string): Promise<Customer | undefined> {
    await this.ensureInitialized();
    let c: Customer | undefined;
    try {
      const db = await getIndexedDb();
      c = await db.get(STORES.CUSTOMERS, id);
    } catch (err) {
      c = this.memoryStore.find((item) => item.id === id);
    }
    if (c && c.dob) {
      return { ...c, age: calculateChronologicalAge(c.dob) };
    }
    return c;
  }

  async save(data: Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'created_at' | 'updated_at'>): Promise<Customer> {
    await this.ensureInitialized();
    const nowIso = new Date().toISOString();
    const dateStr = nowIso.split('T')[0];

    const newEntry: Customer = {
      ...data,
      id: `KH-LOCAL-${Date.now()}`,
      createdAt: dateStr,
      updatedAt: nowIso,
      created_at: nowIso,
      updated_at: nowIso,
      isLocal: true,
    };

    try {
      const db = await getIndexedDb();
      await db.put(STORES.CUSTOMERS, newEntry);
    } catch (err) {
      this.memoryStore.push(newEntry);
    }

    return newEntry;
  }

  async update(id: string, partialData: Partial<Customer>): Promise<Customer | undefined> {
    await this.ensureInitialized();
    const existing = await this.getById(id);
    if (!existing) return undefined;

    const nowIso = new Date().toISOString();
    const updatedEntry: Customer = {
      ...existing,
      ...partialData,
      updatedAt: nowIso,
      updated_at: nowIso,
    };

    try {
      const db = await getIndexedDb();
      await db.put(STORES.CUSTOMERS, updatedEntry);
    } catch (err) {
      const idx = this.memoryStore.findIndex((c) => c.id === id);
      if (idx !== -1) {
        this.memoryStore[idx] = updatedEntry;
      }
    }

    return updatedEntry;
  }

  async delete(id: string): Promise<Customer[]> {
    await this.ensureInitialized();
    try {
      const db = await getIndexedDb();
      await db.delete(STORES.CUSTOMERS, id);
    } catch (err) {
      const idx = this.memoryStore.findIndex((c) => c.id === id);
      if (idx !== -1) {
        this.memoryStore.splice(idx, 1);
      }
    }
    return this.getAll();
  }

  async search(
    query: string,
    category: 'all' | 'main' | 'dependent' = 'all',
    birthdayMonth: 'all' | 'thisMonth' | 'nextMonth' = 'all'
  ): Promise<Customer[]> {
    const all = await this.getAll();
    const q = query.toLowerCase();

    return all.filter((c) => {
      const matchesSearch = !q || c.name.toLowerCase().includes(q) || c.phone.includes(q) || c.dob.includes(q);
      const matchesCategory = category === 'all' || c.category === category;
      let matchesBirthday = true;

      if (birthdayMonth === 'thisMonth') {
        const now = new Date();
        const thisMonthStr = String(now.getMonth() + 1).padStart(2, '0');
        matchesBirthday = c.dob.split('/')[1] === thisMonthStr;
      } else if (birthdayMonth === 'nextMonth') {
        const now = new Date();
        const nextMonth = now.getMonth() === 11 ? 1 : now.getMonth() + 2;
        const nextMonthStr = String(nextMonth).padStart(2, '0');
        matchesBirthday = c.dob.split('/')[1] === nextMonthStr;
      }

      return matchesSearch && matchesCategory && matchesBirthday;
    });
  }
}

/** Export singleton instance */
export const customerRepository = new CustomerRepository();
